import { Grid, MenuItem, Select, SelectChangeEvent, Table, TableBody, Typography } from "@mui/material";
import * as api from "api/Core";
import { DefaultListParameters } from "assets/appConfig";
import { AppMessageBox } from "components/AppMessageBox";
import IconButtonDownload from "components/Buttons/IconButtonDownload";
import { SpinnerIndicator } from "components/CallServer";
import { exportToXLSX } from "components/ExportToExcel";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPagination from "components/MuiExtras/MuiPagination";
import HtmlUtils from "lib/HtmlUtils";
import LibUtils from "lib/LibUtils";
import { AwardType } from "models/ApiEnums";
import { DashboardAwardAnalysisDetailColumn, DashboardGraphModel, DashboardUserModel, PagedResult } from "models/ApiModels";
import moment from "moment";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { DashboardAwardAnalysisProps, DashboardAwardAnalysisUserEvents } from "views/Dashboards/AwardAnalysisDashboard/Common";
import RenderAwardTypeModelList from "views/Dashboards/components/RenderAwardTypeModelList";
import { RenderBarGraph } from "views/Dashboards/components/RenderBarGraph";
import { RenderUserModelList } from "views/Dashboards/components/RenderUserModelList";
import UserListDrawer from "views/Dashboards/components/UserListDrawer";

/////////////////////////////////////////////////////////////////////////////////////

interface IGraphState {
    data: DashboardGraphModel[];
    loading: boolean;
    selectedDay?: Date;
}

interface IGraphProps extends DashboardAwardAnalysisProps, DashboardAwardAnalysisUserEvents {
}

type Props = IGraphProps & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class AwardAnalysisGraphComponent extends React.PureComponent<Props, IGraphState>{

    constructor(props: Props) {
        super(props);

        this.state = {
            data: null,
            loading: true,
            selectedDay: null,
        };
    }

    ///
    private getDateFilter() {

        var de = moment().utc().endOf("month");
        var ds = moment().utc().startOf("month");
        ds.add(-12, "months");

        return { startDate: ds.toDate(), endDate: de.toDate() };
    }

    ///
    private loadData() {
        api.getDashboardAwardAnalysis({
            detailColumn: DashboardAwardAnalysisDetailColumn.GraphAwardMonthly,
            ...this.getDateFilter(),
            ...this.props.filter,
            drillDownFilter: null,
            range: null,
        })
            .then((result: DashboardGraphModel[]) => {
                result.forEach((v) => {
                    v.day = v.day.convertLocalToUtcDate();
                });

                this.setState({ data: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    private handleSelectDay = (dt: Date) => this.setState({ selectedDay: dt });

    private handleCancelDay = () => this.setState({ selectedDay: null });

    public componentDidMount() {
        this.loadData();
    }

    public componentDidUpdate(prevProps: IGraphProps) {
        if (!LibUtils.isEqual(this.props.filter, prevProps.filter)) {
            this.loadData();
        }
    }

    ///
    public render() {
        const { data, loading } = this.state;

        if (data == null && !loading)
            return <AppMessageBox resource="LoadGraphDataFailed" />;

        return (
            <div>
                <MuiContainer margin="dense" >
                    <Typography component="p" variant="h6"><b>{this.props.t("Views.Dashboards.AwardAnalysis.Graph.HeaderTitle").toString().toLocaleUpperCase()}</b></Typography>
                </MuiContainer>

                {
                    loading
                        ? <SpinnerIndicator />
                        : (
                            <>
                                <MuiContainer>
                                    <RenderBarGraph
                                        color="orange"
                                        data={this.state.data}
                                        lastItemFade={0.6}
                                        onSelectDay={this.handleSelectDay}
                                    />
                                </MuiContainer>

                                <AwardAnalysisGraphList
                                    {...this.props}
                                    filter={this.props.filter}
                                    startDate={data == null ? null : data[0].day}
                                    endDate={data == null ? null : data[data.length - 1].day}
                                    selectedDay={this.state.selectedDay}
                                    onCancelDay={this.handleCancelDay}
                                    onSelectUser={this.props.onSelectUser}
                                />
                            </>
                        )
                }
            </div>
        );
    }
}

export const AwardAnalysisGraph = withTranslation()(AwardAnalysisGraphComponent);

/////////////////////////////////////////////////////////////////////////////////////////////

interface IListState {
    dialog: any;
    data: PagedResult<any>;
    offset: number;
    loading: boolean;
    listOption: DashboardAwardAnalysisDetailColumn;
    periodOption: number;
}

interface IListProps extends DashboardAwardAnalysisProps, DashboardAwardAnalysisUserEvents {
    startDate: Date;
    endDate: Date;
    selectedDay: Date;
    onCancelDay: () => void;
}

type GraphListProps = IListProps & WithTranslation;

class AwardAnalysisGraphList extends React.PureComponent<GraphListProps, IListState> {

    constructor(props: GraphListProps) {
        super(props);

        this.state = {
            dialog: null,
            data: null,
            offset: null,
            loading: true,
            listOption: DashboardAwardAnalysisDetailColumn.AwardMonthly,
            periodOption: props.endDate.getTime(),
        };
    }

    ///
    private getDateFilter() {

        if (this.state.periodOption == 0)
            return { startDate: null, endDate: null };

        var ds = moment(this.state.periodOption).utc().startOf("month");
        var de = moment(this.state.periodOption).utc().endOf("month");
        return { startDate: ds.toDate(), endDate: de.toDate() };
    }

    ///
    private loadData(offset: number, scrollToStartOfList: boolean = false) {

        this.setState({ loading: true });

        api.getDashboardAwardAnalysis(
            {
                detailColumn: this.state.listOption,
                ...this.getDateFilter(),
                ...this.props.filter,
                drillDownFilter: null,
                range: { offset, take: DefaultListParameters.viewRecordsByPage },
            })
            .then((result) => {
                this.setState({
                    data: result,
                    loading: false,
                    offset: offset
                }, () => {
                    if (scrollToStartOfList) HtmlUtils.scrollToElement("divList");
                });

            })
            .catch((error) => {
                this.setState({ data: null, loading: false, offset: 0 });
            });
    }


    ///
    private handleExport = () => {

        api.getDashboardAwardAnalysis(
            {
                detailColumn: this.state.listOption,
                ...this.getDateFilter(),
                ...this.props.filter,
                drillDownFilter: null,
                range: { offset: 0, take: 32767 },
            })
            .then((result) => {
                let csv: any;
                const fileName = 'AwardAnalysis-' + moment().toISOString();

                switch (this.state.listOption) {

                    case DashboardAwardAnalysisDetailColumn.AwardMonthly:
                        csv = (result.items as DashboardUserModel[]).map((v, i) => { return { ranking: i + 1, name: v.name, points: v.total }; });
                        break;

                }

                if (csv && csv.length > 0) exportToXLSX(csv, fileName);

            });
    };


    ///
    private handleListOption = (e: SelectChangeEvent<DashboardAwardAnalysisDetailColumn>) => {
        this.setState({ listOption: e.target.value as DashboardAwardAnalysisDetailColumn }, () => this.loadData(0));
    };

    ///
    private handlePeriodOption = (e: SelectChangeEvent<number>) => {
        this.setState({ periodOption: e.target.value as number }, () => this.loadData(0));
    };

    ///
    private getListOptions() {
        var result: Array<{ key: DashboardAwardAnalysisDetailColumn, text: string; }> = [];
        result.push({ key: DashboardAwardAnalysisDetailColumn.AwardMonthly, text: this.props.t("Views.Dashboards.AwardAnalysis.Graph.AwardMonthly") });
        result.push({ key: DashboardAwardAnalysisDetailColumn.AwardType, text: this.props.t("Views.Dashboards.AwardAnalysis.Graph.AwardType") });
        return result;
    }

    ///
    private getPeriodOptions() {
        var result: Array<{ key: Date, text: string; }> = [];

        let dt = this.props.endDate;
        let y = dt.getFullYear();
        let m = dt.getMonth();

        result.push({ key: new Date(0), text: this.props.t("Views.Dashboards.Common.Historic") });

        while (dt >= this.props.startDate) {
            result.push({
                key: dt,
                text: dt == this.props.endDate ? this.props.t("Views.Dashboards.Common.ThisMonth") : moment(dt).format("MMM/YYYY")
            });

            if (--m < 0) { m = 11; y--; }

            dt = new Date(y, m, 1);
        }

        return result;
    }

    ///
    private drillDown(id: AwardType, name: string) {

        this.setState({
            dialog: (
                <UserListDrawer
                    title={this.props.t("Views.Dashboards.AwardAnalysis.Graph.UserListTitle")}
                    subtitle={name}
                    showTotalColumn={true}
                    onGetData={
                        (searchtext, offset) =>
                            api.getDashboardAwardAnalysis({
                                detailColumn: DashboardAwardAnalysisDetailColumn.AwardTypeDrillDown,
                                ...this.getDateFilter(),
                                ...this.props.filter,
                                drillDownFilter: {
                                    id: id,
                                    title: searchtext,
                                },
                                range: { offset: offset, take: 20 },
                            })
                    }
                    onClose={
                        () => this.setState({ dialog: null })
                    }
                />
            )
        });
    }

    ///
    private renderHeader() {
        return (
            <div id="divList" >
                <MuiContainer margin="dense" >
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>

                            <Select
                                value={this.state.listOption}
                                onChange={this.handleListOption}
                            >
                                {
                                    this.getListOptions().map((v, index) => <MenuItem key={index} value={v.key}>{v.text}</MenuItem>)
                                }
                            </Select>

                        </Grid>

                        <Grid item>

                            <Select
                                value={this.state.periodOption}
                                onChange={this.handlePeriodOption}
                            >
                                {
                                    this.getPeriodOptions().map((v, index) => <MenuItem key={index} value={v.key == null ? null : v.key.getTime()}>{v.text}</MenuItem>)
                                }
                            </Select>

                        </Grid>
                    </Grid>
                </MuiContainer>
            </div>
        );
    }

    ///
    private renderListBody() {
        const { data: { items }, offset } = this.state;

        switch (this.state.listOption) {

            case DashboardAwardAnalysisDetailColumn.AwardMonthly:
                return <RenderUserModelList
                    indexOffset={offset}
                    items={items}
                    onSelectUser={this.props.onSelectUser}
                />;

            case DashboardAwardAnalysisDetailColumn.AwardType:
                return <RenderAwardTypeModelList
                    indexOffset={offset}
                    items={items}
                    onDrillDown={(awardType, name) => this.drillDown(awardType, name)}
                />;
        }

        return null;
    }

    ///
    private renderList() {
        const { data: { total }, dialog } = this.state;
        const dataEmpty = this.props.t("Views.Dashboards.AwardAnalysis.Graph.EmptyData");

        return (
            <MuiContainer margin="normal">
                <div>
                    {dialog}
                    <br />
                    {this.renderHeader()}

                    {
                        total == 0
                            ? <MuiMessageBox variant="warning" message={dataEmpty} />
                            : (
                                <>
                                    <MuiContainer>
                                        <Table size="small">
                                            <TableBody >
                                                {this.renderListBody()}
                                            </TableBody>
                                        </Table>
                                    </MuiContainer>
                                    <Grid container justifyContent="center" alignItems="center">
                                        <Grid item xs>
                                            <MuiPagination
                                                limit={DefaultListParameters.viewRecordsByPage}
                                                offset={this.state.offset}
                                                total={total}
                                                onClick={(e, offset) => this.loadData(offset, true)}
                                            />
                                        </Grid>
                                        {
                                            this.state.listOption == DashboardAwardAnalysisDetailColumn.AwardMonthly && 
                                            <Grid item>
                                                <IconButtonDownload onClick={this.handleExport} />
                                            </Grid>
                                        }
                                    </Grid>
                                </>
                            )
                    }
                </div>
            </MuiContainer >
        );
    }

    ///
    private updateListOption() {
        const periodOption = this.props.selectedDay == null ? new Date(0).getTime() : this.props.selectedDay.getTime();
        this.setState({ periodOption }, () => this.loadData(0));
    }

    ///
    private checkForUpdateState(prevProps: GraphListProps) {
        if (
            !LibUtils.isEqual(this.props.filter, prevProps.filter)
            ||
            (this.props.selectedDay != null && this.props.selectedDay != prevProps.selectedDay)
            ||
            (this.props.selectedDay != null && prevProps.selectedDay == null)
            ||
            (this.props.selectedDay == null && prevProps.selectedDay != null)
        ) {
            this.updateListOption();
        }
    }

    ///
    public componentDidUpdate(prevProps: GraphListProps) {
        this.checkForUpdateState(prevProps);
    }

    ///
    public componentDidMount() {
        this.updateListOption();
    }

    ///
    public render() {

        if (this.props.startDate == null)
            return null;

        return (
            <div>
                {
                    (this.state.data == null && !this.state.loading)
                        ? <AppMessageBox resource="LoadListDataFailed" />
                        : this.state.loading
                            ? <SpinnerIndicator />
                            : this.renderList()
                }
            </div>
        );
    }
}